import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { gmpService } from '../../api/gmpService';
import { mapApiItemsToBoardRows } from '../../utils/gmpBoard';

/**
 * Async thunk to fetch grey-market premium for SME IPOs
 * GET /api/market/gmp/sme -- same item shape as GET /api/market/gmp
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchSmeGmp = createAsyncThunk(
  'smeGmp/fetchSmeGmp',
  async (_options, { rejectWithValue }) => {
    try {
      const data = await gmpService.getSmeGmp();
      if (data?.success === false) {
        return rejectWithValue(data.error || 'Failed to fetch SME GMP data');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch SME GMP data');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().smeGmp;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

const initialState = {
  // Raw items from the API; views map them through selectSmeGmpBoardRows
  items: [],
  refreshedAt: null,
  source: null,
  // The API's soft message when it has nothing yet ("GMP data not yet available -- ...")
  note: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const smeGmpSlice = createSlice({
  name: 'smeGmp',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSmeGmp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSmeGmp.fulfilled, (state, action) => {
        const data = action.payload?.data || {};
        state.status = 'succeeded';
        state.items = Array.isArray(data.items) ? data.items : [];
        state.refreshedAt = data.refreshed_at || null;
        state.source = data.source || null;
        state.note = data.error || null;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchSmeGmp.rejected, (state, action) => {
        // Keep previously loaded items so a failed refresh doesn't blank the board
        state.status = 'failed';
        state.error = action.payload || 'Failed to load SME GMP data';
      });
  },
});

// Rows for the SME GMP board page (/sme-ipo-gmp)
export const selectSmeGmpBoardRows = createSelector([(state) => state.smeGmp.items], (items) => mapApiItemsToBoardRows(items));

export default smeGmpSlice.reducer;
